import { useRouter } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import type { Me } from '../api';
import { useI18n } from '../i18n';
import { Alert, PageHead, PanelShell } from '../layout';

/**
 * PDFs and uploaded files inside the app, with a way back (as the PHP panel's viewer): phones, iPhone in
 * particular, show only the first page of a PDF opened directly. PDFs are drawn page by page with pdf.js
 * (loaded only here); pictures are shown as they are; other files (Word) can be downloaded.
 */

export type ViewerType = 'invoice' | 'quote' | 'cash' | 'file' | 'agreement' | 'inbox' | 'drive';

const SOURCES: Record<ViewerType, (id: number) => string> = {
  invoice: (id) => `api/v1/invoices/${id}/pdf`,
  quote: (id) => `api/v1/quotes/${id}/pdf`,
  cash: (id) => `api/v1/cash/entries/${id}/file`,
  file: (id) => `api/v1/invoices/${id}/file`,
  agreement: (id) => `api/v1/agreements/${id}/file`,
  inbox: (id) => `api/v1/inbox/${id}/file`,
  drive: (id) => `api/v1/drive/files/${id}/content`,
};

type State = { kind: 'loading' } | { kind: 'pdf'; pages: number } | { kind: 'image'; url: string } | { kind: 'other' } | { kind: 'error' } | { kind: 'browser' };

export function ViewerPage({ me, type, id, title }: { me: Me; type: ViewerType; id: number; title?: string }) {
  const { t } = useI18n();
  const router = useRouter();
  const pagesRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<State>({ kind: 'loading' });
  const src = SOURCES[type](id);

  useEffect(() => {
    let cancelled = false;
    const urls: string[] = [];
    (async () => {
      const response = await fetch(src, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(String(response.status));
      const blob = await response.blob();
      if (cancelled) return;
      if (blob.type.startsWith('image/')) { const url = URL.createObjectURL(blob); urls.push(url); setState({ kind: 'image', url }); return; }
      if (blob.type !== 'application/pdf') { setState({ kind: 'other' }); return; }
      try {
        const pdfjs = await import('pdfjs-dist');
        pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
        // Relative to the page: the same build also runs under a customer's folder (connected panel).
        const at = (dir: string) => new URL(`pdfjs/${dir}/`, document.baseURI).href;
        const doc = await pdfjs.getDocument({
          data: new Uint8Array(await blob.arrayBuffer()),
          wasmUrl: at('wasm'), standardFontDataUrl: at('standard_fonts'), cMapUrl: at('cmaps'), cMapPacked: true, iccUrl: at('iccs'),
        }).promise;
        if (cancelled) return;
        setState({ kind: 'pdf', pages: doc.numPages });
        const holder = pagesRef.current;
        if (!holder) return;
        holder.replaceChildren();
        const width = Math.min(holder.clientWidth || 800, 1100);
        for (let n = 1; n <= doc.numPages && !cancelled; n++) {
          const page = await doc.getPage(n);
          const base = page.getViewport({ scale: 1 });
          const scale = width / base.width;
          const ratio = window.devicePixelRatio || 1;
          const viewport = page.getViewport({ scale: scale * ratio });
          const canvas = document.createElement('canvas');
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          canvas.style.width = `${Math.floor(viewport.width / ratio)}px`;
          canvas.setAttribute('aria-label', `${n} / ${doc.numPages}`);
          holder.append(canvas);
          await page.render({ canvas, viewport }).promise;
        }
      } catch {
        // pdf.js could not draw it: opened by the browser itself in a new tab, or downloaded (a file inside the page
        // would be blocked by the panel's security policy).
        if (!cancelled) setState({ kind: 'browser' });
      }
    })().catch(() => { if (!cancelled) setState({ kind: 'error' }); });
    return () => { cancelled = true; urls.forEach((u) => URL.revokeObjectURL(u)); };
  }, [src]);

  const back = () => { if (window.history.length > 1) router.history.back(); else void router.navigate({ to: '/' }); };
  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Peržiūra')} title={title || t('Dokumentas')} action={<button type="button" className="button button--ghost" onClick={back}>← {t('Atgal')}</button>} />}>
      <div className="viewer-bar">
        <button type="button" className="button button--ghost button--small" onClick={back}>← {t('Atgal')}</button>
        {state.kind === 'pdf' ? <span className="muted">{t('Puslapių: {n}', { n: state.pages })}</span> : <span />}
        <a className="button button--primary button--small" href={`${src}?download=1`}>{t('Atsisiųsti')}</a>
      </div>
      {state.kind === 'loading' ? <p className="muted">{t('Įkeliama…')}</p> : null}
      {state.kind === 'error' ? <Alert kind="error">{t('Dokumento atidaryti nepavyko.')}</Alert> : null}
      {state.kind === 'other' ? <Alert kind="success">{t('Šio failo peržiūrėti negalima. Atsisiųskite jį.')}</Alert> : null}
      {state.kind === 'image' ? <div className="viewer-pages"><img src={state.url} alt="" /></div> : null}
      {state.kind === 'browser' ? (
        <Alert kind="success">
          {t('Šio PDF čia parodyti nepavyko.')} <a className="text-link" href={src} target="_blank" rel="noreferrer">{t('Atidaryti naršyklėje')}</a>
        </Alert>
      ) : null}
      <div ref={pagesRef} className="viewer-pages" hidden={state.kind !== 'pdf'} />
    </PanelShell>
  );
}
